import {ComponentStore} from '@ngrx/component-store';
import {Injectable} from '@angular/core';
import {ReportChartStoreState} from './report-chart';
import {Observable, of, Subscription, switchMap, withLatestFrom} from 'rxjs';
import {ReportChartOptions} from '../../classes/chart-report';
import {ReportStore} from './report.store';
import {catchError, filter, tap} from 'rxjs/operators';
import {AdRequestOptions} from '../../interfaces/ad-reports';
import {ReportsChartService} from '../../services/reports-chart.service';
import {Serializer} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {selectSite, selectUser} from '../init/init.selectors';
import {ReportStoreState} from './report';
import {User} from '../../interfaces/user';

@Injectable()
export class ReportChartStore extends ComponentStore<ReportChartStoreState> {
  private readonly _dataset$: Observable<Serializer<Site>> = this._store.select(selectSite);
  private readonly _user$: Observable<Serializer<User>> = this._store.select(selectUser);

  public readonly loading$: Observable<boolean> = this.select((state: ReportChartStoreState): boolean => state.loading);
  public readonly loaded$: Observable<boolean> = this.select((state: ReportChartStoreState): boolean => state.loaded);
  public readonly options$: Observable<ReportChartOptions> = this.select((state: ReportChartStoreState): ReportChartOptions => state.options);
  public readonly dimensionValues$: Observable<Array<string>> = this.select((state: ReportChartStoreState): Array<string> => state.dimensionValues);
  public readonly config$: Observable<any> = this.select((state: ReportChartStoreState): any => state.config);

  public readonly load: () => void = this.effect((params$: Observable<void>) => params$.pipe(
    withLatestFrom(this._reportStore.state$, this._dataset$, this._user$),
    filter(([_, reportStoreState]: [void, ReportStoreState, Serializer<Site>, Serializer<User>]): boolean => reportStoreState.submitted),
    tap((): void => this.patchState({ loading: true })),
    switchMap(([_, reportStoreState, dataset, user]: [void, ReportStoreState, Serializer<Site>, Serializer<User>]): Observable<any> => this._service.getChartReport(
      dataset,
      user,
      reportStoreState.uuid,
      reportStoreState.token,
      reportStoreState.requestParamsSubmitted,
      reportStoreState.reportParamsSubmitted,
      reportStoreState.requestOptions
    ).pipe(
      tap(([config, values, options]): void => {
        this.patchState({
          config: config,
          dimensionValues: values,
          options: options,
          loading: false,
          loaded: true
        });
      }),
      catchError(() => {
        this.patchState({ loading: false });

        return of();
      })
    ))
  ));

  private readonly _params$: Subscription = this.effect((): Observable<any> => this._reportStore.reportParamsSubmitted$.pipe(
    tap((): void => this.load())
  ));

  private readonly _options$: Subscription = this.effect((): Observable<Partial<AdRequestOptions>> => this._reportStore.requestOptionsChanges$.pipe(
    tap((): void => this.load())
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _reportStore: ReportStore,
    private readonly _service: ReportsChartService
  ) {
    super({
      config: null,
      options: [],
      dimensionValues: [],
      loading: false,
      loaded: false
    });
  }

  public updateConfig(config: any): void {
    this.patchState({
      config: config
    });
  }

}
